import { CellState } from './game';
import { solve } from './solver';
import {
  Analysis, HintUnit, RuleId, RuleInstance,
  analyze, attacksOf, enumerateFrom,
} from '../shared/rules';

// The player-facing view over the rule engine. The deductions themselves live in
// src/shared/rules.ts (uncapped, un-ranked, prose-free, so the offline puzzle analyzer can run
// them in a fixpoint loop); this module adds the two things only a player needs — the R0
// mistake check and the R1 queen-reach nudge — turns instances into explanations, and ranks
// and caps them for the panel. See HINTS_DESIGN.md.
//
// All hints are sound depth-1 deductions from the current board (no multi-step what-if chains):
// every eliminated cell provably cannot hold a queen given the marks already on the board.

export type { HintUnit } from '../shared/rules';
export { hasProperTightSubset } from '../shared/rules';

export type HintRule = 'R0' | 'R1' | RuleId;
export type HintKind = 'mistake' | 'eliminate' | 'place';

export interface Hint {
  kind: HintKind;
  rule: HintRule;
  title: string;
  explanation: string;
  /** "r,c" keys highlighted as the reason for the deduction. */
  causeCells: string[];
  /** "r,c" keys eliminated (kind 'eliminate') or the single forced cell (kind 'place'). */
  cells: string[];
  /** Rows / columns / regions this deduction reasons about; the board tints and outlines their union. */
  units: HintUnit[];
  /** Ranking weight: eliminations removed, or squares a forced placement would clear. */
  count: number;
}

/** Cell sets the board spotlights for a focused hint (resolved from a Hint via `hintHighlight`). */
export interface HintHighlight {
  /** Live candidate squares that drive the deduction — accented inside the tinted area. */
  cause: Set<string>;
  /** Squares the hint rules out — dimmed with a red ✕. */
  elim: Set<string>;
  /** Forced-placement target(s) — shown as a ghost queen. */
  place: Set<string>;
  /** Rows / columns / regions the hint reasons about — tinted and outlined as one area. */
  rows: Set<number>;
  cols: Set<number>;
  regions: Set<string>;
}

/** Expand a hint's flat cell lists + units into the cell sets the board renders. */
export function hintHighlight(h: Hint): HintHighlight {
  const rows = new Set<number>();
  const cols = new Set<number>();
  const regions = new Set<string>();
  for (const u of h.units) {
    if (u.type === 'row') rows.add(u.index);
    else if (u.type === 'col') cols.add(u.index);
    else regions.add(u.color);
  }
  const cells = new Set(h.cells);
  return {
    cause: new Set(h.causeCells),
    elim: h.kind === 'place' ? new Set() : cells,
    place: h.kind === 'place' ? cells : new Set(),
    rows, cols, regions,
  };
}

const key = (r: number, c: number) => `${r},${c}`;

// Rank ties toward the simpler (lower-numbered) rule for friendlier explanations.
const RULE_ORDER: Record<HintRule, number> = { R0: 0, R1: 1, R2: 2, R3: 3, R4: 4, R5: 5, R6: 6, R7: 7, R8: 8, R9: 9 };

// ---- Presentation ----

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const lineWord = (variant: string) => (variant === 'row' ? 'Row' : 'Column');
const axisWord = (variant: string) => (variant === 'rows' ? 'rows' : 'columns');

function pairExplanation(k1: string, k2: string): string {
  const one = (k: string) => (k === 'color' ? 'a color region' : k === 'row' ? 'a row' : 'a column');
  const many = (k: string) => (k === 'color' ? 'color regions' : k === 'row' ? 'rows' : 'columns');
  const subject = k1 === k2 ? `Two ${many(k1)}` : `${capitalize(one(k1))} and ${one(k2)}`;
  return `${subject} each still need a queen and can't attack each other, so every legal way to place both attacks these squares — ruling them out.`;
}

/**
 * Turn a raw deduction into the card the panel shows. Pure text — no logic lives here.
 * Exported because the Techniques page presents hand-picked instances of its own.
 */
export function present(inst: RuleInstance): Hint {
  const { rule, variant, n } = inst.sig;
  let title: string;
  let explanation: string;

  switch (rule) {
    case 'R2':
      title = `${lineWord(variant)} squeeze`;
      explanation = `Wherever this ${lineWord(variant).toLowerCase()}'s queen goes, it attacks these squares, so they're ruled out.`;
      break;
    case 'R3':
      title = 'Color locked to a line';
      explanation = `This color's queen can only go in one ${variant}, so every other square in that ${variant} is ruled out.`;
      break;
    case 'R4':
      title = `${lineWord(variant)} locked to a color`;
      explanation = `This ${lineWord(variant).toLowerCase()} can only hold one color, so that color is ruled out everywhere else.`;
      break;
    case 'R5':
      title = 'Pigeonhole (colors)';
      explanation = `${n} colors fit within these ${n} ${axisWord(variant)}, so no other color can go there.`;
      break;
    case 'R6':
      title = 'Pigeonhole (lines)';
      explanation = `These ${n} ${axisWord(variant)} hold only ${n} colors, so those colors are ruled out elsewhere.`;
      break;
    case 'R7':
      title = 'Region shadow';
      explanation = 'Every possible spot for this color attacks these squares, so none of them can hold a queen.';
      break;
    case 'R8': {
      const what = variant === 'region' ? 'This color region' : variant === 'row' ? 'This row' : 'This column';
      title = 'Forced queen';
      explanation = `${what} has only one square left, so a queen must go here.`;
      break;
    }
    case 'R9': {
      const [k1, k2] = variant.replace('+ovl', '').split('x');
      title = 'Paired shadow';
      explanation = pairExplanation(k1, k2);
      break;
    }
  }

  return {
    kind: inst.kind, rule, title, explanation,
    causeCells: inst.causeCells, cells: inst.cells, units: inst.units, count: inst.impact,
  };
}

// ---- Mistake detection (R0) ----

function findMistakes(board: CellState[][], colorRegions: string[][], A: Analysis, visibleXs?: Set<string>): Hint[] {
  const size = A.size;

  // Structural: a unit that still needs a queen but has no unmarked square left is unwinnable.
  for (const color of A.openColors) {
    if (!(A.candByColor.get(color)?.length)) {
      const region = cellsOfColor(colorRegions, color);
      return [mistakeHint('a color region has no room left for its queen', region, [{ type: 'region', color }])];
    }
  }
  for (const r of A.openRows) {
    if (!(A.candByRow.get(r)?.length)) {
      return [mistakeHint('a row has no room left for its queen', rowCells(size, r), [{ type: 'row', index: r }])];
    }
  }
  for (const c of A.openCols) {
    if (!(A.candByCol.get(c)?.length)) {
      return [mistakeHint('a column has no room left for its queen', colCells(size, c), [{ type: 'col', index: c }])];
    }
  }

  // Solver-backed: are the current marks compatible with any real solution? Uses the capped
  // solve(); skipped when the cap is hit (we can't be sure we've seen every solution).
  const CAP = 1000;
  const sols = solve(colorRegions, CAP);
  if (sols.length === 0 || sols.length >= CAP) return [];

  const queens: Array<[number, number]> = [];
  const xs: Array<[number, number]> = [];
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (board[r][c] === 'queen') queens.push([r, c]);
      else if (board[r][c] === 'X' || visibleXs?.has(key(r, c))) xs.push([r, c]);
    }
  }
  if (queens.length === 0 && xs.length === 0) return [];

  const consistent = sols.some(sol =>
    queens.every(([r, c]) => sol[r] === c) && !xs.some(([r, c]) => sol[r] === c)
  );
  if (!consistent) {
    return [mistakeHint('your current marks rule out every solution — an X or queen is in the wrong place', queens.map(([r, c]) => key(r, c)), [])];
  }
  return [];
}

function mistakeHint(reason: string, causeCells: string[], units: HintUnit[]): Hint {
  return {
    kind: 'mistake',
    rule: 'R0',
    title: "Something's off",
    explanation: `Check your work — ${reason}.`,
    causeCells,
    cells: [],
    units,
    count: Number.MAX_SAFE_INTEGER,
  };
}

function cellsOfColor(colorRegions: string[][], color: string): string[] {
  const out: string[] = [];
  for (let r = 0; r < colorRegions.length; r++)
    for (let c = 0; c < colorRegions.length; c++)
      if (colorRegions[r][c] === color) out.push(key(r, c));
  return out;
}
const rowCells = (size: number, r: number) => Array.from({ length: size }, (_, c) => key(r, c));
const colCells = (size: number, c: number) => Array.from({ length: size }, (_, r) => key(r, c));

// ---- Main entry ----

/**
 * visibleXs: cells currently *displayed* as Xs without being board marks (the auto-X cells
 * when the Auto Xs setting is on). The engine reasons only from what the player can see, so
 * these count as eliminated while invisibly-attacked squares do not.
 */
export function computeHints(board: CellState[][], colorRegions: string[][], visibleXs?: Set<string>): Hint[] {
  const A = analyze(board, colorRegions, visibleXs);

  const mistakes = findMistakes(board, colorRegions, A, visibleXs);
  if (mistakes.length > 0) return mistakes; // a broken board makes every other deduction unreliable

  const hints: Hint[] = [];

  // R1 — a queen already on the board attacks squares the player hasn't marked yet. Every other
  // rule reasons from visible marks only, so these unmarked squares still count as candidates and
  // hold the whole engine back; surfacing them as a hint (rather than only as the empty-state
  // nudge) gives the player one click to catch the board up. Applying a 'place' hint marks them
  // itself, so this only fires for hand-placed queens (and never with Auto Xs on, where the
  // attacked squares are already displayed as ✕).
  //
  // Not a rule instance: it derives nothing new, it just writes down what a queen already says.
  // The offline analyzer classifies these marks as bookkeeping, which is why they live here and
  // not in the enumerator.
  for (const [r, c] of A.queens) {
    const elim = [...attacksOf(A, r, c)].filter(k => A.candKeys.has(k));
    if (elim.length === 0) continue;
    hints.push({
      kind: 'eliminate', rule: 'R1', title: "Queen's reach",
      explanation: 'This queen attacks these squares — its row, its column, the squares touching it, and the rest of its color — so none of them can hold a queen.',
      causeCells: [key(r, c)], cells: elim, units: [], count: elim.length,
    });
  }

  for (const inst of enumerateFrom(A)) hints.push(present(inst));

  return rank(hints);
}

// Unmarked queen attacks (R1) rank first: they're the most elementary deduction on the board and
// every other rule is held back until they're marked, so clearing them is always the right next
// move. Forced placements (R8) come next: a unit down to a single candidate is the most obvious,
// satisfying move — "place a queen here". Single-unit eliminations
// (R2–R7) come next, by count desc with ties toward the simpler rule. The pairwise rule R9 is
// strictly more general and would otherwise bury the simple techniques the hints are meant to
// teach, so it sits in its own tier last — surfacing only when it adds eliminations the simpler
// rules miss (the hard-puzzle case). Drop instances whose eliminations are already covered by a
// higher-ranked one; cap the panel.
//
// The sort is stable, so hints tying on (tier, count, rule) keep the enumerator's emission order.
function rank(hints: Hint[]): Hint[] {
  const tier = (h: Hint) => (h.rule === 'R1' ? 0 : h.kind === 'place' ? 1 : h.rule === 'R9' ? 3 : 2);
  hints.sort((a, b) => tier(a) - tier(b) || b.count - a.count || RULE_ORDER[a.rule] - RULE_ORDER[b.rule]);

  const kept: Hint[] = [];
  for (const h of hints) {
    const cells = new Set(h.cells);
    const covered = kept.some(k => {
      if (k.kind !== h.kind) return false;
      const ks = new Set(k.cells);
      return [...cells].every(c => ks.has(c));
    });
    if (!covered) kept.push(h);
    if (kept.length >= 5) break;
  }
  return kept;
}
